import api from './api';

/**
 * The fields a supplier profile must have before the portal will let them past
 * the profile page. Mirrors server/utils/supplierProfile.js — keep the two in
 * step.
 *
 * `page` says where the field is edited, so a caller can send someone straight
 * to the form that fixes it.
 */
export const REQUIRED_PROFILE_FIELDS = [
  { key: 'authorizedPerson.name',            label: 'Contact name',                page: 'contact' },
  { key: 'authorizedPerson.relationship',    label: 'Relationship to the company', page: 'contact' },
  { key: 'authorizedPerson.idPassportNumber',label: 'ID or passport number',       page: 'contact' },
  { key: 'authorizedPerson.phone',           label: 'Phone number',                page: 'contact' },
  { key: 'authorizedPerson.email',           label: 'Contact email',               page: 'contact' },
  { key: 'supplierName',                     label: 'Company name',                page: 'company' },
  { key: 'registeredCountry',                label: 'Registered country',          page: 'company' },
  { key: 'companyRegistrationNumber',        label: 'Company registration number', page: 'company' },
  { key: 'companyEmail',                     label: 'Company email',               page: 'company' },
  { key: 'companyPhysicalAddress',           label: 'Physical address',            page: 'company' },
];

/**
 * Which required fields are still blank.
 *
 * This exists because "incomplete" used to be a bare boolean: the edit forms
 * accepted a blank address, reported "saved successfully", and the route guard
 * then bounced the supplier back to the profile page without ever saying which
 * field was at fault. Returning the list is what lets the UI tell them.
 *
 * @returns {Array<{key, label, page}>} empty when the profile is complete
 */
export const getMissingProfileFields = (supplier) => {
  if (!supplier) return [...REQUIRED_PROFILE_FIELDS];

  const address = supplier.companyPhysicalAddress;
  const fullAddress = address
    ? `${address.street || ''}, ${address.city || ''}, ${address.country || ''}${address.postalCode ? `, ${address.postalCode}` : ''}`.replace(/^,\s*|,\s*$/g, '')
    : supplier.physicalAddress || '';

  const present = {
    'authorizedPerson.name': supplier.authorizedPerson?.name,
    'authorizedPerson.relationship': supplier.authorizedPerson?.relationship,
    'authorizedPerson.idPassportNumber': supplier.authorizedPerson?.idPassportNumber,
    'authorizedPerson.phone': supplier.authorizedPerson?.phone,
    'authorizedPerson.email': supplier.authorizedPerson?.email,
    supplierName: supplier.supplierName,
    registeredCountry: supplier.registeredCountry || address?.country,
    companyRegistrationNumber: supplier.companyRegistrationNumber,
    companyEmail: supplier.companyEmail,
    companyPhysicalAddress: fullAddress,
  };

  return REQUIRED_PROFILE_FIELDS.filter((f) => {
    const value = present[f.key];
    return !(typeof value === 'string' ? value.trim() : value);
  });
};

/**
 * Check if supplier profile information is complete
 * @param {Object} supplier - Supplier data object
 * @param {Object} user - User data object
 * @returns {boolean} - True if all required information is filled
 */
export const isSupplierProfileComplete = (supplier, user) => {
  if (!user || user.role !== 'supplier') {
    return true; // Non-suppliers don't need profile check
  }
  return getMissingProfileFields(supplier).length === 0;
};

/**
 * Fetch supplier data and check if profile is complete
 * @param {Object} user - User data object
 * @returns {Promise<boolean>} - True if profile is complete
 */
export const checkSupplierProfileComplete = async (user) => {
  if (!user || user.role !== 'supplier') {
    return true; // Non-suppliers don't need profile check
  }

  try {
    const response = await api.get('/suppliers');
    const suppliers = response.data.data || [];
    
    if (suppliers.length === 0) {
      return false; // No supplier data means profile is incomplete
    }

    const supplier = suppliers[0];
    return isSupplierProfileComplete(supplier, user);
  } catch (error) {
    console.error('Error checking supplier profile:', error);
    return false; // On error, assume incomplete
  }
};

/**
 * Fetch the supplier record and report which required fields are still blank.
 * Returns null when the caller is not a supplier.
 */
export const fetchMissingProfileFields = async (user) => {
  if (!user || user.role !== 'supplier') return null;
  try {
    const response = await api.get('/suppliers');
    const suppliers = response.data.data || [];
    return getMissingProfileFields(suppliers[0]);
  } catch (error) {
    console.error('Error checking supplier profile:', error);
    return null;
  }
};
